import { useState, useCallback, useEffect } from 'react';
import { C, FONT, StatTile, SectionHeader, fmtSessionDate, calendarDaysAgo } from './adminShared';
import { useAuth } from '../../hooks/useAuth';
import { ALL_CONTACTS } from './AdminUsers';
import { JOURNAL_PROMPTS_BY_DAY } from '../../utils/constants';
import {
  getAdminStatsFull,
  getAdminFunnelFull,
  getAdminCourseProgress,
  getAdminAlerts,
  getAdminRevenueSummary,
  sendMissedBmsFollowup,
  sendLoginReminder,
  getMissedBmsJourney, getAdminRySalesStats,
} from '../../utils/api';

const DISMISSED_KEY = 'admin_dismissed_alerts_v2';

// ─── Build alert items from the /api/admin/alerts response ─────────
function buildAlertItems(alertsData) {
  if (!alertsData) return [];
  const items = [];
  if (alertsData.notLoggedIn > 0) {
    items.push({
      key: 'not_logged_in',
      count: alertsData.notLoggedIn,
      label: `client${alertsData.notLoggedIn !== 1 ? 's' : ''} purchased but haven't logged in yet — send an access reminder`,
      type: 'warn',
    });
  }
  if (alertsData.notBooked > 0) {
    items.push({
      key: 'day7_not_booked',
      count: alertsData.notBooked,
      label: `client${alertsData.notBooked !== 1 ? 's' : ''} completed the course but haven't booked their BMS call`,
      type: 'warn',
    });
  }
  if (alertsData.notAttended > 0) {
    items.push({
      key: 'booked_not_attended',
      count: alertsData.notAttended,
      label: `client${alertsData.notAttended !== 1 ? 's' : ''} missed their BMS session — send a personal follow-up message`,
      type: 'err',
    });
  }
  return items;
}

// ─── Components ────────────────────────────────────────────────────
const AlertBadge = ({ item, onDismiss }) => {
  const isWarn = item.type === 'warn';
  const bg = isWarn ? C.amberBg : C.redBg;
  const col = isWarn ? '#92650A' : C.red;
  const dot = isWarn ? C.amber : C.red;
  const border = isWarn ? '#F5D69A' : '#F5C6C0';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: col }}>
          {item.count != null ? `${item.count} ` : ''}{item.label}
        </span>
      </div>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: col, opacity: 0.5, padding: 0, lineHeight: 1 }}>×</button>
    </div>
  );
};

// ─── Helpers for the journey timeline ──────────────────────────────────────

// Format an ISO string in the admin viewer's local timezone, human-readable.
const fmtAdminLocal = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-GB', {
      weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
};

// Per-status colour tokens for the timeline. Complete → green; incomplete →
// neutral accent; na → very-light gray. We avoid STAGE_META because that
// describes user funnel stages, not journey-step status.
const STATUS_TOKENS = {
  complete:   { icon: '✓',  fg: C.green,  bg: C.greenBg,  iconBg: C.green,    iconFg: '#FFF8F7' },
  incomplete: { icon: '◯',  fg: C.accent, bg: C.barTrack, iconBg: '#fff',     iconFg: C.accent  },
  na:         { icon: '—',  fg: C.accent, bg: '#fff',     iconBg: '#fff',     iconFg: C.border  },
};

// ─── Journey timeline (9 stage rows from /missed-bms-journey) ───────────
const JourneyTimeline = ({ journey }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {(journey || []).map(stage => {
      const tok = STATUS_TOKENS[stage.status] || STATUS_TOKENS.incomplete;
      const ts  = fmtAdminLocal(stage.timestamp);
      let extraStr = null;
      if (stage.key === 'bms_booked' && stage.extra?.sessionDate) {
        // The viewer's time first. The customer's zone name used to sit right
        // after it, as if the time were theirs — it never was. Now their own
        // time is shown AS theirs, only when their zone is known and differs.
        const localStr = fmtAdminLocal(stage.extra.sessionDate);
        const theirTz = stage.extra.sessionTimezone;
        let theirStr = '';
        if (theirTz) {
          try {
            const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (theirTz !== viewerTz) {
              const t = new Date(stage.extra.sessionDate).toLocaleString('en-GB', {
                timeZone: theirTz, hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
              });
              theirStr = ` · their time ${t} (${theirTz})`;
            }
          } catch { theirStr = ` · their zone ${theirTz}`; }
        }
        extraStr = `Session: ${localStr || stage.extra.sessionDate}${theirStr}`;
      }
      return (
        <div key={stage.key} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 10px', borderRadius: 6, background: tok.bg,
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: tok.iconBg, color: tok.iconFg,
            fontSize: 12, fontWeight: 700, flexShrink: 0,
            border: `1px solid ${tok.iconBg === '#fff' ? C.border : tok.iconBg}`,
          }}>{tok.icon}</div>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: C.heading }}>{stage.label}</span>
              {stage.detail && (
                <span style={{ fontSize: 11, color: tok.fg, fontStyle: 'italic' }}>{stage.detail}</span>
              )}
            </div>
            {(ts || extraStr) && (
              <div style={{ fontSize: 10.5, color: C.accent, marginTop: 1 }}>
                {ts}{ts && extraStr ? ' · ' : ''}{extraStr}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// ─── Per-day accordion in the course-progress section ───────────────────
const DayAccordion = ({ day }) => {
  const [open, setOpen] = useState(false);

  // Header summary — completion ticks for the 4 sub-tasks
  const tick = (b) => b
    ? <span style={{ color: C.green, fontWeight: 700 }}>✓</span>
    : <span style={{ color: C.border }}>·</span>;

  const noRowEver = !day.dayComplete
    && !day.videoComplete && !day.journalComplete
    && !day.checkinComplete && !day.meditationComplete
    && day.journalEntries.length === 0
    && !day.checkin;

  const headerLabel = noRowEver ? 'Not started' : (day.dayComplete ? 'Complete' : 'In progress');
  const headerColor = noRowEver ? C.accent : (day.dayComplete ? C.green : '#92650A');

  // Lookup prompt text from the constants file. Day 6 has labels per prompt.
  const promptsForDay = JOURNAL_PROMPTS_BY_DAY?.[day.dayNum] || [];

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, marginBottom: 6, background: '#fff' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '8px 12px', textAlign: 'left',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          fontFamily: FONT,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.heading, minWidth: 44 }}>Day {day.dayNum}</span>
          <span style={{ fontSize: 11, color: headerColor, fontWeight: 600 }}>{headerLabel}</span>
          {!noRowEver && (
            <span style={{ fontSize: 11, color: C.accent, display: 'flex', gap: 6 }}>
              <span>vid {tick(day.videoComplete)}</span>
              <span>jrn {tick(day.journalComplete)}</span>
              <span>chk {tick(day.checkinComplete)}</span>
              <span>med {tick(day.meditationComplete)}</span>
            </span>
          )}
        </div>
        <span style={{
          fontSize: 14, color: C.accent,
          display: 'inline-block',
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease',
        }}>›</span>
      </button>
      {open && (
        <div style={{ padding: '4px 14px 14px', borderTop: `1px solid ${C.border}` }}>
          {noRowEver ? (
            <div style={{ fontSize: 12, color: C.accent, padding: '8px 0' }}>—</div>
          ) : (
            <>
              {day.checkin && (
                <div style={{ fontSize: 11.5, color: C.text, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                  Check-in · Calm <strong>{day.checkin.calm ?? '–'}</strong>
                  {' · '}Connected <strong>{day.checkin.connected ?? '–'}</strong>
                  {' · '}Clarity <strong>{day.checkin.clarity ?? '–'}</strong>
                  {' · '}Total <strong>{day.checkin.total ?? '–'}</strong>
                </div>
              )}
              {day.journalEntries.length === 0 ? (
                <div style={{ fontSize: 12, color: C.accent, padding: '10px 0' }}>No journal entries</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 10 }}>
                  {day.journalEntries.map(entry => {
                    const promptObj = promptsForDay[entry.promptIndex];
                    const promptText = promptObj?.q || `Prompt ${entry.promptIndex + 1}`;
                    const promptLabel = promptObj?.label;
                    return (
                      <div key={entry.promptIndex} style={{ paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
                        {promptLabel && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                            {promptLabel}
                          </div>
                        )}
                        <div style={{ fontSize: 10.5, color: C.accent, marginBottom: 4 }}>
                          {promptText}
                        </div>
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                          {entry.answer}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Missed-BMS inline list (under the "booked_not_attended" alert) ───
// One row per client genuinely missed (after the call_attended back-fill
// + Zoom-webhook/no-show-cron mirror, this list is honest).
//
// The whole row (except the Send button) is the expand/collapse hit area.
// The Send button uses stopPropagation so clicking it never toggles expand.
// Journey data is lazy-loaded on first expand and cached at the parent
// level so collapsing and re-expanding doesn't re-fetch.
const MissedBmsRow = ({ row, expanded, journey, loading, error, onToggle, onRetry }) => {
  const { user } = useAuth();
  const [send, setSend] = useState({ status: 'idle', message: null });

  const handleSend = useCallback(async (e) => {
    e.stopPropagation();
    setSend({ status: 'sending', message: null });
    try {
      const { data } = await sendMissedBmsFollowup(row.userId);
      if (data?.sent) {
        setSend({ status: 'sent', message: `✓ Follow-up sent to ${row.firstName || row.email}` });
      } else if (data?.reason === 'unsubscribed') {
        setSend({ status: 'skipped', message: `Skipped — ${row.firstName || row.email} has unsubscribed` });
      } else if (data?.reason === 'error') {
        setSend({ status: 'error', message: `Could not send — ${data.error || 'unknown error'}` });
      } else {
        setSend({ status: 'error', message: 'Could not send — unexpected response' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Network error';
      setSend({ status: 'error', message: `Could not send — ${msg}` });
    }
  }, [row.userId, row.firstName, row.email]);

  // Format session date in viewer's local timezone
  const dateStr = row.sessionDate
    ? new Date(row.sessionDate).toLocaleString('en-GB', {
        weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

  // Right-hand side switches between button and status message based on state
  let rightSide;
  if (send.status === 'sent') {
    rightSide = (
      <span style={{ fontSize: 12, fontWeight: 600, color: C.green }}>
        {send.message}
      </span>
    );
  } else if (send.status === 'skipped') {
    rightSide = (
      <span style={{ fontSize: 12, fontWeight: 600, color: '#92650A' }}>
        {send.message}
      </span>
    );
  } else {
    const isSending = send.status === 'sending';
    rightSide = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {send.status === 'error' && (
          <span style={{ fontSize: 11, color: C.red }}>{send.message}</span>
        )}
        {/* Owner-only: viewers see the missed-BMS row but cannot send the follow-up */}
        {user?.role === 'owner' && (
        <button
          onClick={handleSend}
          disabled={isSending}
          style={{
            padding: '6px 12px',
            background: isSending ? C.barTrack : C.heading,
            color: isSending ? C.accent : '#FFF8F7',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: isSending ? 'default' : 'pointer',
            fontFamily: FONT,
            whiteSpace: 'nowrap',
          }}
        >
          {isSending ? 'Sending…' : 'Send follow-up'}
        </button>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', border: `1px solid ${C.border}`,
      borderRadius: 8, marginBottom: 6, overflow: 'hidden',
    }}>
      {/* Header — clickable for expand/collapse */}
      <div
        onClick={() => onToggle(row.userId)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '10px 14px', cursor: 'pointer',
          background: expanded ? C.veryLightBrown : '#fff',
          transition: 'background 0.15s ease',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>
            {row.firstName || ''}{row.firstName && row.lastName ? ' ' : ''}{row.lastName || ''}
          </span>
          <span style={{ fontSize: 11, color: C.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.email} · {dateStr}
          </span>
        </div>
        {/* Chevron — rotates when expanded */}
        <span style={{
          fontSize: 16, color: C.accent, marginRight: 6,
          display: 'inline-block',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease',
        }}>›</span>
        {rightSide}
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{
          padding: '14px 16px 16px',
          background: C.veryLightBrown,
          borderTop: `1px solid ${C.border}`,
        }}>
          {loading && (
            <div style={{ fontSize: 12, color: C.accent, padding: '8px 0' }}>Loading journey…</div>
          )}
          {error && (
            <div style={{ fontSize: 12, color: C.red, padding: '8px 0' }}>
              {error}{' · '}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onRetry(row.userId); }}
                style={{ color: C.heading, textDecoration: 'underline', cursor: 'pointer' }}
              >Retry</span>
            </div>
          )}
          {!loading && !error && journey && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Funnel journey
              </div>
              <JourneyTimeline journey={journey.journey} />

              <div style={{ height: 1, background: C.border, margin: '18px 0 12px' }} />

              <div style={{ fontSize: 11, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Daily progress + journal
              </div>
              {(journey.courseProgress || []).map(day => (
                <DayAccordion key={day.dayNum} day={day} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Purchased-but-not-logged-in row ───────────────────────────────────────
// Mirrors MissedBmsRow: clickable header (single-open expand), owner-gated
// action button using e.stopPropagation() so it never toggles the row, plus an
// always-shown "Email personally" mailto link. Tracks its own reminded + send
// state locally, and disables the reminder once sent so a second password reset
// can't be triggered by accident.
const NotLoggedInRow = ({ row, expanded, onToggle }) => {
  const { user } = useAuth();
  const [reminded, setReminded] = useState(!!row.loginReminderSent);
  const [send, setSend] = useState({ status: 'idle', message: null });

  const handleSend = useCallback(async (e) => {
    e.stopPropagation();
    if (reminded || send.status === 'sending') return;
    setSend({ status: 'sending', message: null });
    try {
      const { data } = await sendLoginReminder(row.userId);
      if (data?.sent) {
        setReminded(true);
        setSend({ status: 'sent', message: `✓ Reminder sent to ${row.firstName || row.email}` });
      } else if (data?.skipped && data?.reason === 'already_reminded') {
        setReminded(true);
        setSend({ status: 'skipped', message: 'Already reminded' });
      } else if (data?.skipped && data?.reason === 'already_logged_in') {
        setReminded(true);
        setSend({ status: 'skipped', message: 'They have already logged in' });
      } else if (data?.skipped && data?.reason === 'unsubscribed') {
        setSend({ status: 'skipped', message: 'Skipped — unsubscribed' });
      } else if (data?.skipped) {
        setSend({ status: 'skipped', message: `Skipped — ${data.reason || 'not sent'}` });
      } else {
        setSend({ status: 'error', message: 'Could not send — unexpected response' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Network error';
      setSend({ status: 'error', message: `Could not send — ${msg}` });
    }
  }, [row.userId, row.firstName, row.email, reminded, send.status]);

  const name = `${row.firstName || ''}${row.firstName && row.lastName ? ' ' : ''}${row.lastName || ''}`.trim() || row.email;
  const createdStr = row.createdAt
    ? new Date(row.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

  const statusTag = reminded
    ? <span style={{ fontSize: 11, fontWeight: 700, color: C.green, background: C.greenBg, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>Reminder sent</span>
    : <span style={{ fontSize: 11, fontWeight: 700, color: '#92650A', background: C.amberBg, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>Not reminded yet</span>;

  // Always-shown mailto link (no owner gate — it just opens the admin's client).
  const mailtoLink = (
    <a
      href={`mailto:${row.email}`}
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: 12, fontWeight: 600, color: C.heading, textDecoration: 'underline', whiteSpace: 'nowrap' }}
    >
      Email personally
    </a>
  );

  // Owner-only reminder control: enabled only when not yet reminded.
  let sendControl = null;
  if (user?.role === 'owner') {
    if (reminded) {
      sendControl = <span style={{ fontSize: 11, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap' }}>Already sent</span>;
    } else {
      const isSending = send.status === 'sending';
      sendControl = (
        <button
          onClick={handleSend}
          disabled={isSending}
          style={{
            padding: '6px 12px',
            background: isSending ? C.barTrack : C.heading,
            color: isSending ? C.accent : '#FFF8F7',
            border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: isSending ? 'default' : 'pointer', fontFamily: FONT, whiteSpace: 'nowrap',
          }}
        >
          {isSending ? 'Sending…' : 'Send login reminder'}
        </button>
      );
    }
  }

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
      {/* Header — clickable for expand/collapse */}
      <div
        onClick={() => onToggle(row.userId)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '10px 14px', cursor: 'pointer',
          background: expanded ? C.veryLightBrown : '#fff', transition: 'background 0.15s ease',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.heading, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {name}
            {row.isComp && (
              <span style={{ fontSize: 10, fontWeight: 700, color: C.blue, background: C.blueBg, borderRadius: 5, padding: '1px 6px' }}>Comp</span>
            )}
          </span>
          <span style={{ fontSize: 11, color: C.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.email}</span>
        </div>
        {statusTag}
        <span style={{
          fontSize: 16, color: C.accent, display: 'inline-block',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease',
        }}>›</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {send.status === 'error' && <span style={{ fontSize: 11, color: C.red }}>{send.message}</span>}
          {(send.status === 'sent' || send.status === 'skipped') && (
            <span style={{ fontSize: 11, fontWeight: 600, color: send.status === 'sent' ? C.green : '#92650A' }}>{send.message}</span>
          )}
          {mailtoLink}
          {sendControl}
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '12px 16px 14px', background: C.veryLightBrown, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.text, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div><strong style={{ color: C.heading }}>Account created:</strong> {createdStr}</div>
          <div><strong style={{ color: C.heading }}>Login reminder:</strong> {reminded ? 'Sent' : 'Not sent yet'}</div>
          <div><strong style={{ color: C.heading }}>Comp account:</strong> {row.isComp ? 'Yes' : 'No'}</div>
        </div>
      )}
    </div>
  );
};

// One step of the journey. `pct` is the share of the step ABOVE, already
// computed and already suppressed-where-meaningless by the backend — the row
// renders whatever it is given and decides nothing. Tighter spacing than the
// old 8-row card: this list is 14 rows and Ben reads it on a phone.
// "since 14 Aug" / "all time". Shown on a row ONLY where its period differs
// from the row above — most rows share one and fourteen labels would be noise.
const periodLabel = (iso) => {
  if (!iso) return 'all time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `since ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

const FunnelBar = ({ label, value, pct, pctOf, pctOfLabel, pctAlso, pctAlsoOf, pctAlsoLabel, since, note, shaded, first, last }) => {
  return (
    <div style={{
      marginBottom: 9,
      // A quiet grouping tint for the post-purchase half, using the existing
      // palette's track colour at low opacity so it reads as a group rather
      // than a warning. Padding is added ONLY when shaded, and the negative
      // margin cancels it, so an unshaded row occupies exactly the space it did
      // before this change.
      // The tint must read as ONE block, so shaded rows carry NO bottom margin
      // between them — their backgrounds touch. The 9px of rhythm an unshaded
      // row gets from marginBottom is supplied here as padding instead, so row
      // spacing is unchanged and only the last row of the group re-opens the
      // gap to whatever follows.
      ...(shaded ? {
        background: `${C.barTrack}80`,
        margin: `0 -10px ${last ? 9 : 0}px`,
        padding: `${first ? 8 : 4}px 10px ${last ? 8 : 5}px`,
        borderTopLeftRadius: first ? 8 : 0,
        borderTopRightRadius: first ? 8 : 0,
        borderBottomLeftRadius: last ? 8 : 0,
        borderBottomRightRadius: last ? 8 : 0,
      } : {}),
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text, flex: 1, minWidth: 0 }}>
          {label}
          {since && <span style={{ fontSize: 10, fontWeight: 500, color: C.accent, marginLeft: 6 }}>{since}</span>}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexShrink: 0 }}>
          {/* The percentage itself stays on this line, beside the count. What
              it is a share OF has moved to its own line below — see there. */}
          {pct != null && (
            <span style={{ fontSize: 11, color: C.accent, whiteSpace: 'nowrap' }}
                  title={pctOfLabel ? `${pct}% of ${pctOfLabel}${pctOf != null ? ` (${pctOf.toLocaleString()})` : ''}` : undefined}>
              ({pct}%)
            </span>
          )}
          <span style={{ fontSize: 13, fontWeight: 700, color: C.heading, minWidth: 32, textAlign: 'right' }}>{value.toLocaleString()}</span>
        </div>
      </div>
      {/* ── WHAT THE PERCENTAGE IS A SHARE OF, IN WORDS ──────────────────
          Ben: "Instead of saying 56% of 118, say what it is. So 56% of unique
          visitors to masterclass... It can be in smaller writing."

          ON ITS OWN LINE, not beside the count, and that is forced rather than
          chosen: this card is 343px wide on his phone and the row above already
          carries a label, a percentage and a figure. "of Unique visitors to
          masterclass" cannot share that line at any font size without pushing
          the count off. Given a full-width line, no name has to be abbreviated
          — the longest here is 36 characters — so the reader never has to
          decode a shortening to work out which row is meant.

          THE LABEL IS PRINTED EXACTLY AS THE ROW ABOVE CARRIES IT, capitals and
          all. Lower-casing it to read more like a sentence would turn "BMS
          booked" into "bMS booked", and matching the text to its row matters
          more than the sentence flowing.

          pctOfLabel comes from the endpoint, which derives it from the row it
          actually divided by — so this can never name a denominator the maths
          did not use. */}
      {pct != null && pctOfLabel && (
        <div style={{ fontSize: 10, color: C.accent, lineHeight: 1.4, marginTop: 1 }}>
          {pct}% of {pctOfLabel}
        </div>
      )}
      {/* ── THE SAME COUNT AGAINST A SECOND ROW ───────────────────────────
          Only the two "Clicked CTA" rows send one today: their clicker count
          as a share of the "Opted in" row on this same list, the figure the
          Pages & Masterclass cards print under their Clicked CTA tiles. Same
          line, same size, same wording as the primary share above it, so the
          two read as a pair; the count is the row's own figure on the right.
          The endpoint suppresses it under the primary's own rules — zero
          denominator, count above denominator, or a different period — so
          nothing here can print a share over 100%. Renders nothing when not
          sent, and the primary line above it is untouched. */}
      {pctAlso != null && pctAlsoLabel && (
        <div style={{ fontSize: 10, color: C.accent, lineHeight: 1.4, marginTop: 1 }}
             title={`${pctAlso}% of ${pctAlsoLabel}${pctAlsoOf != null ? ` (${pctAlsoOf.toLocaleString()})` : ''}`}>
          {pctAlso}% of {pctAlsoLabel}
        </div>
      )}

      {/* A caveat that belongs to ONE row rather than the whole card. Only
          "Landed on opt-in page" sends one today — it is the single row that
          counts a narrower population than the rest of the list, and the note
          names the campaigns it leaves out. The endpoint generates that text
          from the exclusion's own list, so this renders whatever it is sent and
          decides nothing. Renders nothing when no note is sent. */}
      {note && (
        <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', lineHeight: 1.4, marginTop: 1 }}>
          {note}
        </div>
      )}
    </div>
  );
};

const DayBar = ({ day, count, maxCount, isAmber }) => {
  const w = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, width: 36, flexShrink: 0 }}>Day {day}</div>
      <div style={{ flex: 1, height: 10, background: C.barTrack, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${w}%`, background: isAmber ? C.amber : C.chartBar, borderRadius: 6, transition: 'width 0.4s ease' }} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: isAmber ? C.amber : C.heading, width: 28, textAlign: 'right', flexShrink: 0 }}>{count}</div>
      {isAmber && <span style={{ fontSize: 10, color: C.amber, fontWeight: 700, flexShrink: 0 }}>← biggest drop</span>}
    </div>
  );
};

export const AdminOverview = () => {
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); }
    catch { return []; }
  });

  const [statsFull,      setStatsFull]      = useState(null);
  const [funnelFull,     setFunnelFull]     = useState([]);
  const [courseProgress, setCourseProgress] = useState([]);
  const [alerts,         setAlerts]         = useState(null);
  const [revenueSummary, setRevenueSummary] = useState(null);
  // Paid/gift/comp split behind the RY Purchases tile. /ry-sales-stats has
  // returned this since bebbb44 — nothing here widens an endpoint.
  const [rySales,       setRySales]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);

  // ── Missed-BMS row expansion state (only one expanded at a time) ──
  // journeyData/Loading/Error are keyed by userId so collapsing+reopening
  // does not refetch, and each row tracks its own load/error state.
  const [expandedUserId,        setExpandedUserId]        = useState(null);
  const [journeyData,           setJourneyData]           = useState({});
  const [journeyLoadingUserId,  setJourneyLoadingUserId]  = useState(null);
  const [journeyErrorByUser,    setJourneyErrorByUser]    = useState({});

  // ── Not-logged-in row expansion (independent single-open, its own state) ──
  const [expandedNliUserId, setExpandedNliUserId] = useState(null);
  const toggleNliRow = useCallback((userId) => {
    setExpandedNliUserId(prev => (prev === userId ? null : userId));
  }, []);

  // Fetch the journey for one userId. Cached at the parent so collapse+reopen
  // doesn't refetch. Used by both the toggle handler and the inline Retry.
  const fetchJourneyFor = useCallback(async (userId) => {
    setJourneyLoadingUserId(userId);
    setJourneyErrorByUser(prev => ({ ...prev, [userId]: null }));
    try {
      const { data } = await getMissedBmsJourney(userId);
      setJourneyData(prev => ({ ...prev, [userId]: data }));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Could not load journey';
      setJourneyErrorByUser(prev => ({ ...prev, [userId]: msg }));
    } finally {
      setJourneyLoadingUserId(curr => (curr === userId ? null : curr));
    }
  }, []);

  const toggleMissedBmsRow = useCallback((userId) => {
    setExpandedUserId(prev => {
      if (prev === userId) return null;       // collapse current
      // Opening a (possibly different) row — fire fetch if needed
      if (!journeyData[userId] && journeyLoadingUserId !== userId) {
        fetchJourneyFor(userId);
      }
      return userId;
    });
  }, [journeyData, journeyLoadingUserId, fetchJourneyFor]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, f, c, a, r, rs] = await Promise.all([
          getAdminStatsFull(),
          getAdminFunnelFull(),
          getAdminCourseProgress(),
          getAdminAlerts(),
          getAdminRevenueSummary(),
          getAdminRySalesStats(),
        ]);
        if (!active) return;
        setStatsFull(s.data);
        setFunnelFull(f.data || []);
        setCourseProgress(c.data || []);
        setAlerts(a.data);
        setRevenueSummary(r.data);
        setRySales(rs.data);
      } catch (err) {
        if (active) setError(err.response?.data?.error || err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const dismissAlert = useCallback((key) => {
    setDismissed(prev => {
      const next = [...prev, key];
      try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // ── Derived values ──────────────────────────────────────────────
  const alertItems = buildAlertItems(alerts);
  const visibleAlerts = alertItems.filter(a => !dismissed.includes(a.key));


  // "Biggest drop" flags content-day disengagement only (Days 1–6). Day 7 is
  // Call Day (video-only, no /complete step), so exclude it and the Day 6 → 7
  // transition from the drop scan — otherwise the natural fall-off into Call Day
  // would always win. All 7 bars still render below.
  let amberDay = null, maxDrop = 0;
  for (let i = 1; i < courseProgress.length; i++) {
    if (courseProgress[i].day > 6) continue; // skip Call Day (and the 6→7 drop)
    const drop = courseProgress[i - 1].count - courseProgress[i].count;
    if (drop > maxDrop) { maxDrop = drop; amberDay = courseProgress[i].day; }
  }
  const progressMax = courseProgress.length > 0 ? courseProgress[0].count : 0;

  // ── Stat tile values ────────────────────────────────────────────
  const totalOptins       = statsFull?.totalOptins ?? 0;
  const totalEnrolled     = statsFull?.totalEnrolled ?? 0;
  // How many of those enrolments were gift-code redemptions. Same window as the
  // tile above it (both launch-floored), so the two always agree — a gifted
  // count from a different period sitting under this number would be worse than
  // no count at all. Comp is deliberately NOT shown: comps are out of the funnel
  // picture entirely, which is the whole point of this change.
  const giftedEnrolled    = rySales?.giftPurchasers ?? null;
  const callsBooked       = statsFull?.callsBooked ?? 0;
  const callsAttended     = statsFull?.callsAttended ?? 0;
  const bmsAttendanceRate = statsFull?.bmsAttendanceRate ?? 0;
  const activeThisWeek    = statsFull?.activeThisWeek ?? 0;

  // UUU count from funnel array ("UUU Client" stage)
  const uuuCount = (funnelFull.find(s => s.label === 'UUU Client')?.value) ?? 0;

  // Computed metrics
  const fmt$ = (n) => n == null ? '—' : '$' + Math.round(n).toLocaleString();

  const totalRevenue     = revenueSummary?.totalAllTime ?? null;
  const thisMonthRevenue = revenueSummary?.thisMonth    ?? null;

  const bmsCloseRate = callsAttended > 0
    ? Math.round((uuuCount / callsAttended) * 100) + '%'
    : '—';

  const earningsPerLead = (revenueSummary?.totalAllTime != null && totalOptins > 0)
    ? fmt$(revenueSummary.totalAllTime / totalOptins)
    : '—';

  if (loading) {
    return (
      <div style={{ fontFamily: FONT, textAlign: 'center', padding: '60px 0', fontSize: 13, color: C.accent }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>

      {/* Error banner */}
      {error && (
        <div style={{ background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.red }}>
          ✗ Failed to load overview data: {error}
        </div>
      )}

      {/* Alerts */}
      {visibleAlerts.length > 0 ? (
        <div style={{ marginBottom: 28 }}>
          <SectionHeader title="Requires Your Action" sub="Only things the system can't handle for you" />
          {visibleAlerts.map(a => (
            <div key={a.key}>
              <AlertBadge item={a} onDismiss={() => dismissAlert(a.key)} />
              {a.key === 'not_logged_in' && alerts?.notLoggedInList?.length > 0 && (
                <div style={{ marginTop: -2, marginBottom: 12, paddingLeft: 14 }}>
                  {alerts.notLoggedInList.map(row => (
                    <NotLoggedInRow
                      key={row.userId}
                      row={row}
                      expanded={expandedNliUserId === row.userId}
                      onToggle={toggleNliRow}
                    />
                  ))}
                </div>
              )}
              {a.key === 'booked_not_attended' && alerts?.notAttendedList?.length > 0 && (
                <div style={{ marginTop: -2, marginBottom: 12, paddingLeft: 14 }}>
                  {alerts.notAttendedList.map(row => (
                    <MissedBmsRow
                      key={row.userId}
                      row={row}
                      expanded={expandedUserId === row.userId}
                      journey={journeyData[row.userId] || null}
                      loading={journeyLoadingUserId === row.userId}
                      error={journeyErrorByUser[row.userId] || null}
                      onToggle={toggleMissedBmsRow}
                      onRetry={fetchJourneyFor}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ marginBottom: 28, padding: '13px 18px', background: C.greenBg, border: '1px solid #A8D5C0', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: C.green }}>All clear — nothing requires your personal attention right now</span>
        </div>
      )}

      {/* Stat tiles */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatTile label="Total Opt-ins"    value={totalOptins.toLocaleString()}      sub="All-time leads" />
        <StatTile
          label="RY Purchases"
          value={totalEnrolled.toLocaleString()}
          sub={giftedEnrolled == null ? 'Enrolled clients' : `Enrolled clients · ${giftedEnrolled} gifted`}
        />
        <StatTile label="BMS Booked"       value={callsBooked.toLocaleString()}       sub={`${callsAttended} attended`} />
        <StatTile label="Total Revenue"    value={fmt$(totalRevenue)}               sub="All-time · Stripe" accent={C.green} />
        <StatTile label="BMS Attendance"   value={`${bmsAttendanceRate}%`}           sub="Booked → attended" accent={C.green} />
        <StatTile label="BMS Close Rate"   value={bmsCloseRate}                     sub="Attended → UUU" accent={C.green} />
        <StatTile label="Earnings Per Lead" value={earningsPerLead}                 sub="Revenue ÷ opt-ins" accent={C.heading} />
        <StatTile label="Active This Week" value={activeThisWeek.toLocaleString()}   sub="Logged in last 7d" />
      </div>

      {/* Funnel + Course Progress */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 28 }}>
        {/* CHANGE 3 — narrower on DESKTOP so each row reads as one unit: on a
            wide screen the numbers sat far from their labels and the eye had to
            travel. maxWidth caps it; flex/minWidth are untouched, so below
            520px the container is still narrower than the cap and MOBILE IS
            BYTE-IDENTICAL — 375px renders exactly as before. Width only: no
            font, colour, spacing or row-order change. */}
        <div style={{ flex: 2, minWidth: 280, maxWidth: 520, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '22px 24px' }}>
          <SectionHeader title="Funnel Overview" />
          {/* CHANGE 4 — everything from "Course started" down is POST-PURCHASE
              delivery; everything above it is pre-purchase acquisition. Two
              halves of the business, so the lower half sits on a quiet tint.
              Detected from the row's OWN period rather than by counting rows or
              matching a label: the delivery rows are exactly the ones the
              endpoint marks all-time (trackingSince null), which is the same
              distinction, already computed server-side. Add or reorder a row and
              the shading follows. Tint only — no heading, no border, no reorder,
              no figure touched. */}
          {funnelFull.length > 0
            ? funnelFull.map((s, i) => (
                <FunnelBar
                  key={s.label}
                  label={s.label}
                  value={s.value}
                  pct={s.pctOfAbove}
                  pctOf={s.pctOfValue}
                  pctOfLabel={s.pctOfLabel}
                  pctAlso={s.pctOfAlso}
                  pctAlsoOf={s.pctOfAlsoValue}
                  pctAlsoLabel={s.pctOfAlsoLabel}
                  note={s.note}
                  shaded={!s.trackingSince}
                  first={i === 0 || Boolean(funnelFull[i - 1].trackingSince)}
                  last={i === funnelFull.length - 1}
                  // Only when this row's period differs from the one above it.
                  since={i === 0 || funnelFull[i - 1].trackingSince !== s.trackingSince
                    ? periodLabel(s.trackingSince)
                    : null}
                />
              ))
            : <div style={{ fontSize: 12, color: C.accent }}>No funnel data yet</div>
          }
        </div>
        <div style={{ flex: 1, minWidth: 220, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '22px 24px' }}>
          <SectionHeader title="Course Progress" sub={amberDay ? `Biggest drop at Day ${amberDay}` : 'Users completing each day'} />
          {courseProgress.length > 0
            ? courseProgress.map(d => (
                <DayBar key={d.day} day={d.day} count={d.count} maxCount={progressMax} isAmber={d.day === amberDay} />
              ))
            : <div style={{ fontSize: 12, color: C.accent }}>No progress data yet</div>
          }
          {amberDay && (
            <div style={{ marginTop: 10, fontSize: 11, color: '#92650A', background: C.amberBg, border: '1px solid #F5D69A', borderRadius: 8, padding: '8px 12px' }}>
              Drop-off between days shows where clients disengage — consider reviewing that day's content
            </div>
          )}
        </div>
      </div>

      {/* Upcoming BMS Sessions */}
      {(() => {
        const upcoming = ALL_CONTACTS
          .filter(c => c.callBooked && c.sessionDate && new Date(c.sessionDate) > new Date())
          .sort((a, b) => new Date(a.sessionDate) - new Date(b.sessionDate));
        if (upcoming.length === 0) return null;
        return (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '22px 24px', marginBottom: 28 }}>
            <SectionHeader title="Upcoming BMS Sessions" sub={`${upcoming.length} session${upcoming.length !== 1 ? 's' : ''} coming up`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {upcoming.map(c => {
                // Calendar days ahead on the VIEWER'S calendar: a session later today reads
                // "Today" (it used to round hours up, so "Today" could never appear).
                const daysAway = -calendarDaysAgo(c.sessionDate);
                const formFilled = !!c.bmsForm;
                return (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, flexWrap: 'wrap',
                    padding: '10px 14px', borderRadius: 8,
                    background: daysAway <= 2 ? C.amberBg : C.blueBg,
                    border: `1px solid ${daysAway <= 2 ? '#F5D69A' : '#B3D4F0'}`,
                  }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>
                        {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                      </span>
                      <span style={{ fontSize: 12, color: C.blue, marginLeft: 10 }}>
                        {fmtSessionDate(c.sessionDate)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: daysAway <= 2 ? '#92650A' : C.blue }}>
                        {daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `In ${daysAway} days`}
                      </span>
                      <span style={{ fontSize: 11, color: formFilled ? C.green : C.accent }}>
                        {formFilled ? '✓ Form submitted' : 'Form not submitted'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Revenue summary — figures from Revenue tab (stats-full doesn't include revenue) */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '22px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <SectionHeader title="Revenue Summary" sub="Full breakdown in the Revenue tab" />
            <div style={{ display: 'flex', gap: 32 }}>
              <div>
                <div style={{ fontSize: 11, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>All Time</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: C.green }}>{fmt$(totalRevenue)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>This Month</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: C.heading }}>{fmt$(thisMonthRevenue)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Active This Week</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: C.heading }}>{activeThisWeek}</div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: C.accent, alignSelf: 'flex-end' }}>Currency: USD</div>
        </div>
      </div>
    </div>
  );
};
